import React, { useState } from "react";
import { useMenu } from "../hooks/useMenu";
import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Icon,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import MUILink from "@mui/material/Link";
import { Link } from "react-router-dom";
import makeStyles from "@mui/styles/makeStyles";
import { useAuth } from "../../providers/AuthProvider";
import { Trans, useTranslation } from "react-i18next";
import useRoutes from "../../hooks/useRoutes";
import { usePortal } from "../../../Providers/PortalProvider";
import SharePictureSets from "../../Components/MyAccount/SharePictureSets";
import EpmsAnalytics from "../../../../../apps/webshop/assets/Analytics/EpmsAnalytics";
import useDialogBackButton from "../../../hooks/useDialogBackButton";

const useStyles = makeStyles((theme) => ({
  appBarRoot: {
    backgroundColor: "white",
    color: "black",
  },
  mainMenu: {
    "& > .muiPaper-root": {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      maxWidth: "90vw",
    },
    "& .menu-active": {
      //backgroundColor: "rgba(0,0,0,0.05)",
    },
    "&.menu-active": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
    "& .menu-item-with-children": {
      textTransform: "uppercase",
      "& span": {
        fontWeight: "bolder",
        fontSize: "1em",
      },
    },
  },
  menuButton: {
    flexGrow: 0,
  },
  menuTopLevel: {
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    borderTop: "1px solid #ececec",
    "&.menu-active": {
      color: "#000",
    },
  },
  menuParent: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  spacer: {
    flexGrow: 1,
  },
  itemText: {
    "& span": {
      fontWeight: 700,
      fontSize: "1em",
    },
  },
  menuLevelOne: {
    paddingLeft: "20px",
  },
  menuLevelTwo: {
    paddingLeft: "30px",
    paddingBottom: 0,
    color: "#666",
    "& span": {},
  },
  logoContainer: {
    maxHeight: "50px",
    "& img": {
      objectFit: "contain",
      height: "100%",
    },
  },
  smallHeader: {
    fontSize: "12px",
    color: theme.palette.primary.dark,
    fontWeight: 500,
  },
  headerSubText: {
    fontSize: "0.875rem",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    lineHeight: 1.75,
    "&  .MuiIcon-root": {
      padding: 0,
    },
    "& .MuiButton-text": {
      padding: 0,
      textTransform: "none",
      fontWeight: "normal",
    },
  },
  currentLanguageContainer: {
    borderLeft: `2px solid ${theme.palette.primary.light}`,
    paddingLeft: theme.spacing(2),
  },
}));

// Add `replace` only to internal React Router links so navigating from the
// drawer consumes its synthetic back-button history entry instead of leaving a
// same-URL duplicate. External (<a>) and modal items are passed through
// untouched so `replace` never lands on a plain anchor.
const withReplace = (link) =>
  link && link.component === Link ? { ...link, replace: true } : link;

const MainMenu = ({ menuOpen, onClose }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const classes = useStyles();
  const menu = useMenu();
  const [modal, setModal] = useState(null);

  // Hardware/browser back closes the navigation drawer instead of navigating
  // away from the page (e.g. out to the pre-login screen).
  useDialogBackButton(menuOpen, onClose);

  const toggleExpandedItem = (e, id) => {
    e.preventDefault();
    e.stopPropagation();

    setExpandedItems((expandedItems) => {
      let newExpandedItems = { ...expandedItems };
      if (!newExpandedItems[id]) {
        newExpandedItems[id] = true;
      } else {
        delete newExpandedItems[id];
      }
      return newExpandedItems;
    });
  };

  return (
    <Drawer open={menuOpen} onClose={onClose} className={classes.mainMenu}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
        pb={2}
        ml={2}
        mr={0}
      >
        <div className={classes.logoContainer}>
          <img src={window.PAGE_SETTINGS.logo} />
        </div>
        <div>
          <IconButton onClick={onClose}>
            <Icon color="primary">menu_open</Icon>
          </IconButton>
        </div>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <UserInfo handleClose={onClose} />
        <Languages handleClose={onClose} />
      </Box>
      <List className={classes.menuParent}>
        {menu.map((item) => {
          const isActive = !!expandedItems[item.id];
          const activeClass = isActive ? "menu-active" : "";
          const hasChildren = item.children && item.children.length > 0;

          const getOnClickHandler = (item) => {
            if (item.type === "modal") {
              return () => {
                setModal(item.target);
              };
            }
            if (item.type === "cookiesettings") {
              return (event) => {
                const opened =
                  window.isEpmsAnalyticsFeatureEnabled &&
                  window.epmsAnalytics &&
                  window.epmsAnalytics.openConsentSettings();
                if (opened) {
                  event.preventDefault();
                }
                onClose();
              };
            }
            if (item.type === "openchatbot") {
              return () => {
                if (window.openChatWidget) {
                  window.openChatWidget();
                }
                onClose();
              };
            }
            // Assume parent with sub menu?
            if (item.type === "parent-without-link") {
              return (e) => {
                toggleExpandedItem(e, item.id);
              };
            }
            return onClose;
          };

          if (item.type === "spacer") {
            return (
              <React.Fragment key={item.id}>
                <ListItem
                  className={`${classes.menuTopLevel} ${classes.spacer}`}
                ></ListItem>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={item.id}>
              <ListItem
                {...withReplace(item.link)}
                className={`${classes.menuTopLevel} ${activeClass} ${item.type}`}
                onClick={getOnClickHandler(item)}
              >
                <ListItemText
                  primary={item.title}
                  className={classes.itemText}
                />
                {hasChildren && (
                  <IconButton
                    onClick={(e) => toggleExpandedItem(e, item.id)}
                    data-testid="toggle-submenu-button"
                  >
                    {isActive ? (
                      <Icon>expand_less</Icon>
                    ) : (
                      <Icon>expand_more</Icon>
                    )}
                  </IconButton>
                )}
              </ListItem>

              {hasChildren && (
                <Collapse in={isActive} timeout="auto" unmountOnExit>
                  <List data-testid="collapsable-sub-menu">
                    {item.children.map((item) => {
                      const withChildrenClass =
                        item.children && item.children.length > 0
                          ? "menu-item-with-children"
                          : "";
                      return (
                        <React.Fragment key={item.id}>
                          <ListItem
                            key={item.id}
                            {...withReplace(item.link)}
                            onClick={getOnClickHandler(item)}
                            className={`${classes.menuLevelOne} ${activeClass} ${withChildrenClass}`}
                          >
                            <ListItemText primary={item.title} />
                          </ListItem>
                          {item.children &&
                            item.children.map((item) => {
                              const component = item.m ? Link : "a";

                              return (
                                <ListItem
                                  key={item.id}
                                  component={component}
                                  {...(item.m ? { replace: true } : {})}
                                  href={item.url}
                                  to={item.url}
                                  onClick={getOnClickHandler(item)}
                                  className={`${classes.menuLevelTwo} ${activeClass}`}
                                >
                                  <ListItemText primary={item.title} />
                                </ListItem>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      {modal === "share-picture-sets" && (
        <SharePictureSets
          onClose={() => {
            setModal(null);
            onClose();
          }}
        />
      )}
    </Drawer>
  );
};

export const UserInfo = ({ handleClose: onClose }) => {
  const { user } = useAuth();
  const classes = useStyles();
  const { t } = useTranslation();
  const routes = useRoutes();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (user && !user.isAnonymousUser) {
    return (
      <div>
        <div className={classes.smallHeader}>
          {t("Hi, %s", {
            postProcess: "sprintf",
            sprintf: [user.name || user.emailAddress],
          })}
        </div>
        <div className={classes.headerSubText}>
          <Button
            onClick={handleClick}
            color="primary"
            aria-controls="my-account-menu"
            aria-haspopup="true"
            data-testid="my-account-menu-button"
          >
            <Trans>My account</Trans>
            <Icon>expand_more</Icon>
          </Button>
        </div>
        <MyAccountMenu
          anchorEl={anchorEl}
          onClose={handleClose}
          onMenuClick={onClose}
        />
      </div>
    );
  }

  return (
    <div>
      <div className={classes.smallHeader}>
        <Trans>Already a user?</Trans>
      </div>
      <div className={classes.headerSubText}>
        <MUILink component={Link} to={routes.login.path} onClick={onClose}>
          <Trans>Log in</Trans>
        </MUILink>
      </div>
    </div>
  );
};

const MyAccountMenu = ({ anchorEl, onMenuClick, onClose }) => {
  const routes = useRoutes();
  const { logout } = useAuth();

  // Hardware/browser back closes the open menu instead of navigating away.
  useDialogBackButton(Boolean(anchorEl), onClose);

  return (
    <Menu
      id="my-account-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      <MenuItem
        component={Link}
        replace
        to={routes.myaccount.path}
        onClick={onMenuClick}
        autoFocus
      >
        <Trans>My account</Trans>
      </MenuItem>
      <MenuItem
        component={Link}
        replace
        to={routes["myaccount.picturesets"].path}
        onClick={onMenuClick}
      >
        <Trans>My picture sets</Trans>
      </MenuItem>
      <MenuItem
        component={Link}
        replace
        to={routes["myaccount.orders"].path}
        onClick={onMenuClick}
      >
        <Trans>Orders</Trans>
      </MenuItem>
      <MenuItem onClick={logout} data-testid="log-out-menu-item">
        <Trans>Log out</Trans>
      </MenuItem>
    </Menu>
  );
};

export const Languages = ({ handleClose }) => {
  const portal = usePortal();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { languages } = portal.locale;
  const { user, setLanguage } = useAuth();
  const { i18n } = useTranslation();

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleSetLang = (language) => {
    setAnchorEl(null);
    setLanguage(language).finally(() => {
      handleClose();
    });
  };

  if (!languages || languages.length == 0) {
    return null;
  }

  let currentLang = languages.find((lang) => {
    if (user && !user.isAnonymousUser) {
      return lang.long === user.lang;
    }
    return i18n.language === lang.long.replace("_", "-");
  });

  if (!currentLang) {
    currentLang = languages.find((lang) => {
      return lang.long === portal.locale.default_language;
    });
  }

  return (
    <div className={classes.currentLanguageContainer}>
      <div className={classes.smallHeader}>
        <Trans>Language</Trans>
      </div>
      <div className={classes.headerSubText}>
        <Button
          onClick={handleClick}
          color="primary"
          aria-controls="languages-menu"
          aria-haspopup="true"
          aria-expanded={anchorEl !== null ? "true" : "false"}
        >
          {currentLang.name}
          <Icon>expand_more</Icon>
        </Button>
      </div>
      <LanguageSelector
        languages={languages}
        anchorEl={anchorEl}
        handleSetLang={handleSetLang}
        handleClose={() => setAnchorEl(null)}
      />
    </div>
  );
};

const LanguageSelector = ({
  languages,
  anchorEl,
  handleClose,
  handleSetLang,
}) => {
  // Hardware/browser back closes the open menu instead of navigating away.
  useDialogBackButton(Boolean(anchorEl), handleClose);

  return (
    <Menu
      id="languages-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
    >
      {languages.map((language) => {
        return (
          <MenuItem
            key={language.long}
            onClick={() => handleSetLang(language)}
          >
            {language.name}
          </MenuItem>
        );
      })}
    </Menu>
  );
};
export default MainMenu;
